'use client';

import { useEffect, useState } from 'react';

import SearchCardLoading from './search-card-loading-component';
import { SearchSchedulesList } from './search-schedules-list.component';

import SearchError from '@/app/(main)/(pages)/busca/[slug]/_components/search-error/search-error.component';
import { getTelemedProcedure, TelemedProcedure } from '@/app/(main)/(pages)/busca/[slug]/_services/get-telemed-procedure.ts.service';
import { getNearCitySchedules, NearCitySchedules } from '@/app/(main)/(pages)/busca/[slug]/_services/get-near-city-schedules.service';
import { getCityBranches, CityBranches } from '@/app/(main)/(pages)/busca/[slug]/_services/get-city-branches.service';
import { SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { CodesOrSlugsResponse } from '@/app/(main)/(pages)/busca/[slug]/_types/codes-or-slugs-response.type';
import { useLoading } from '@/hooks/use-loading.hook';
import { useUserLocation } from '@/hooks/use-user-location.hook';
import { useSearchFilter } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-search-filters.hook';
import { SearchType } from '@/types/most-search-type';
import { getSchedulesFromSearchApi } from '@/app/(main)/(pages)/busca/[slug]/_services/get-schedules-from-search-api.service';
import { getAvailableFilters } from '@/app/(main)/(pages)/busca/[slug]/_utils/filter-availability.util';
import { getSearchModality } from '@/app/(main)/(pages)/busca/[slug]/_utils/search-modality.util';
import { classifySearchError, isRetryableSearchError, SearchErrorType } from '@/app/(main)/(pages)/busca/[slug]/_utils/search-error.util';
import { minResultsToHideSuggestions } from '@/utils/consts/search-schedules.consts';
import { getLogger } from '@/logger/logger';

const logger = getLogger('search.schedules-list');

interface SearchSchedulesProps {
  procedures: CodesOrSlugsResponse;
  isLaboratorialSearch?: boolean;
  isVaccineSearch?: boolean;
}

export default function SearchSchedules({
  procedures,
  isLaboratorialSearch,
  isVaccineSearch,
}: SearchSchedulesProps) {
  const { userLocation } = useUserLocation();
  const { filters } = useSearchFilter();

  const modality = getSearchModality({
    searchType: procedures[0]?.searchType,
    isLaboratorialSearch,
    isVaccineSearch,
  });
  const { distance, ageGroup, dayTime, professionalGender, followUp, onlyScheduled, minDate, maxDate }
    = getAvailableFilters(filters, modality);

  const { isLoading, startLoading, stopLoading } = useLoading(true);
  const [viewTracked, setViewTracked] = useState(false);
  const [schedules, setSchedules] = useState<SearchPartnerData[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [telehealthSuggestions, setTelehealthSuggestions] = useState<TelemedProcedure | null>(null);
  const [nearCitySchedules, setNearCitySchedules] = useState<NearCitySchedules | null>(null);
  const [cityBranches, setCityBranches] = useState<CityBranches | null>(null);
  const [errorType, setErrorType] = useState<SearchErrorType | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  const retry = () => setRetryToken((token) => token + 1);

  const fetchSchedules = async () => {
    const slugs = procedures.map((p) => p.slug);

    const { schedules, hasMore } = await getSchedulesFromSearchApi({
      slugs,
      latitude: userLocation?.latitude,
      longitude: userLocation?.longitude,
      city: userLocation?.city,
      state: userLocation?.state,
      distance,
      orderBy: filters.orderBy,
      ageGroup,
      dayTime,
      professionalGender,
      minDate,
      maxDate,
      followUp,
      onlyScheduled,
      page: 1,
      searchType: procedures[0]?.searchType,
    });

    return { schedules, hasMore };
  };

  const fetchTelehealth = async (searchType: SearchType) => {
    if (searchType === SearchType.LABORATORIAL_EXAM) {
      return { telehealthSuggestions: null };
    }

    const procedureCodes = procedures.map((p) => p.code);
    const telehealthSuggestions = await getTelemedProcedure({ procedureCodes });

    return { telehealthSuggestions };
  };

  const fetchSuggestions = async (loadedSchedules: SearchPartnerData[], hasMore: boolean) => {
    const procedure = procedures[0];

    const { telehealthSuggestions } = await fetchTelehealth(procedure.searchType as SearchType);
    setTelehealthSuggestions(telehealthSuggestions);

    const hasFewCityResults = !hasMore && loadedSchedules.length < minResultsToHideSuggestions;

    if (!userLocation || !hasFewCityResults) {
      return;
    }

    const [nearCity, branches] = await Promise.all([
      getNearCitySchedules({
        procedureCodes: procedures.map((p) => p.code),
        searchType: procedure.searchType as SearchType,
        city: userLocation.city,
        state: userLocation.state,
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
      }),
      getCityBranches({
        searchType: procedure.searchType as SearchType,
        city: userLocation.city,
        state: userLocation.state,
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
      }),
    ]);

    setNearCitySchedules(nearCity);
    setCityBranches(branches);
  };

  useEffect(() => {
    const fetchData = async () => {
      startLoading();
      setErrorType(null);

      try {
        const { schedules, hasMore } = await fetchSchedules();
        setSchedules(schedules);
        setHasMore(hasMore);

        await fetchSuggestions(schedules, hasMore).catch((error) => {
          logger.error('Failed to fetch search suggestions', { search: { event: 'suggestions_failed' }, error });
        });
      } catch (error) {
        const type = classifySearchError(error);
        logger.error('Failed to fetch search schedules', { search: { event: 'schedules_failed', type }, error });
        setErrorType(type);
      } finally {
        stopLoading();
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userLocation,
    procedures,
    filters.orderBy,
    distance,
    ageGroup,
    dayTime,
    professionalGender,
    minDate,
    maxDate,
    followUp,
    onlyScheduled,
    retryToken,
  ]);

  useEffect(() => {
    setViewTracked(false);
  }, [userLocation, procedures]);

  if (isLoading) {
    return <SearchCardLoading />;
  }

  if (isRetryableSearchError(errorType)) {
    return <SearchError type={errorType} onRetry={retry} titleAs="h2" />;
  }

  const procedure = procedures[0];

  return (
    <SearchSchedulesList
      schedules={schedules}
      hasMore={hasMore}
      searchType={procedure.searchType}
      modality={modality}
      telehealthSuggestions={telehealthSuggestions}
      nearCitySchedules={nearCitySchedules}
      cityBranches={cityBranches}
      procedureName={procedure.name}
      proceduresResearched={procedures}
      shouldTrackView={!viewTracked}
      onViewTracked={() => setViewTracked(true)}
    />
  );
}
